/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("TableController", ["ControllerBase", "ControllerFactory"], function (context, cls) {
  /**
   * @class TableController
   * @memberOf classes
   * @extends classes.ControllerBase
   */
  class TableController extends cls.ControllerBase /** @lends classes.TableController.prototype */ {
    static __name = "TableController";

    /** @type {Object|null} */
    _storedSettingsKeys = null;

    // specific variables used for multirow selection
    /** @type {number} */
    multiRowSelectionRoot = -1;
    /** @type {boolean} */
    updateMultiRowSelectionRoot = false;

    /** @type {boolean} */
    nativeVerticalScroll = true;

    // forceDefaultSettings is assigned in _initBehaviors (invoked during super() construction),
    // so it is intentionally not declared as a class field to avoid clobbering it after super().
    // _isTreeView is derived from the bindings anchor on demand (needed during super()).

    /**
     * @param {ControllerBindings} bindings
     */
    constructor(bindings) {
      super(bindings);
      this._initStoredSettings();
    }

    /**
     * @inheritDoc
     */
    _createWidget(type) {
      let parentPageWidget = null;
      const uiWidget = this.getUINode().getController().getWidget();
      const parentPageNode = this.getAnchorNode().getAncestor("Page");
      if (parentPageNode) {
        parentPageWidget = parentPageNode.getController().getWidget();
      }

      return cls.WidgetFactory.createWidget(
        "RTable",
        {
          appHash: this.getAnchorNode().getApplication().applicationHash,
          appWidget: this.getAnchorNode().getApplication().getUI().getWidget(),
          auiTag: this.getAnchorNode().getId(),
          uiWidget: uiWidget,
          folderPageWidget: parentPageWidget,
          isTreeView: this.getNodeBindings().anchor.isTreeView(),
          isMultiColumnSortSupported: this.getAnchorNode().getApplication().isMultiColumnSortSupported()
        },
        this.getAnchorNode()
      );
    }

    /**
     * Initialize Stored Setting
     * @private
     */
    _initStoredSettings() {
      const node = this.getNodeBindings().anchor;

      // Build stored settings key
      const formName = node.getAncestor("Form").attribute("name");
      const tableName = node.attribute("tabName");

      this._storedSettingsKeys = {
        formName,
        tableName
      };
    }

    /**
     * @inheritDoc
     */
    _initBehaviors() {
      super._initBehaviors();

      // These behaviors should stay added at first
      // WARNING : DO NOT ADD BEHAVIORS BEFORE
      this._addBehavior(cls.LayoutInfoVMBehavior);
      this._addBehavior(cls.StyleVMBehavior);
      // END WARNING

      // pseudo-selector behaviors
      this._addBehavior(cls.FocusCurrentCellPseudoSelectorBehavior);
      this._addBehavior(cls.OffsetPseudoSelectorBehavior);
      this._addBehavior(cls.Reverse4STBehavior);

      // vm behaviors
      this._addBehavior(cls.TableDialogTypeVMBehavior);
      this._addBehavior(cls.StretchVMBehavior);
      this._addBehavior(cls.EnabledVMBehavior);
      this._addBehavior(cls.HiddenVMBehavior);
      this._addBehavior(cls.ColorVMBehavior);
      this._addBehavior(cls.BackgroundColorVMBehavior);
      this._addBehavior(cls.FontFamilyVMBehavior);
      this._addBehavior(cls.FocusOnFieldVMBehavior);
      this._addBehavior(cls.VisibleRowsVMBehavior);
      this._addBehavior(cls.TableSortVMBehavior);
      this._addBehavior(cls.MultiRowSelectionVMBehavior);
      this._addBehavior(cls.WantFixedPageSizeVMBehavior);
      this._addBehavior(cls.PageSizeVMBehavior);
      this._addBehavior(cls.NativeScrollVMBehavior);
      this._addBehavior(cls.FlippedVMBehavior);
      this._addBehavior(cls.NoFilterVMBehavior);
      this._addBehavior(cls.TabNameVMBehavior);

      // ui behaviors
      this._addBehavior(cls.NativeScrollUIBehavior);
      this._addBehavior(cls.OnLayoutUIBehavior);
      this._addBehavior(cls.RowAndSelectionUIBehavior);
      this._addBehavior(cls.TableFrozenUIBehavior);
      this._addBehavior(cls.TableResetToDefaultUIBehavior);
      this._addBehavior(cls.RequestFocusUIBehavior);
      this._addBehavior(cls.RowActionUIBehavior);
      this._addBehavior(cls.TableSortUIBehavior);
      if (this.getNodeBindings().anchor.isTreeView()) {
        this._addBehavior(cls.TreeItemKeyExpandUIBehavior);
        this._addBehavior(cls.TreeItemToggleUIBehavior);
      }
      this._addBehavior(cls.RowBoundUIBehavior);
      this._addBehavior(cls.TableDndUIBehavior);
      this._addBehavior(cls.HaveRowActionsVMBehavior);
      this._addBehavior(cls.TableClickOnContainerUIBehavior);
      this._addBehavior(cls.OnCopyArrayUIBehavior);
      this._addBehavior(cls.FocusChildUIBehavior);
      // Pagination nav WIP
      //this._addBehavior(cls.ScrollOffsetUIBehavior);

      // 4st behaviors
      this._addBehavior(cls.TableType4STBehavior);
      this._addBehavior(cls.FrozenColumns4STBehavior);
      this._addBehavior(cls.TableHeader4STBehavior);
      this._addBehavior(cls.ShowGrid4STBehavior);
      this._addBehavior(cls.AllowWebSelection4STBehavior);
      this._addBehavior(cls.RowAspect4STBehavior);
      this._addBehavior(cls.Highlight4STBehavior);
      this._addBehavior(cls.Border4STBehavior);
      this._addBehavior(cls.ResizeFillsEmptySpace4STBehavior);
      this._addBehavior(cls.RowActionTrigger4STBehavior);
      this._addBehavior(cls.ReduceFilter4STBehavior);
      this._addBehavior(cls.RowHover4STBehavior);
      this._addBehavior(cls.AlternateRows4STBehavior);
      this._addBehavior(cls.DefaultTTFColor4STBehavior);
      this._addBehavior(cls.SummaryLine4STBehavior);
      this._addBehavior(cls.ImageBorderRadius4STBehavior);

      // Last behavior as it update frequently
      this._addBehavior(cls.TableCurrentVMBehavior);

      // should be set before table column widgets creation
      this.forceDefaultSettings = this.getAnchorNode().getStyleAttribute("forceDefaultSettings") === "yes";
    }

    /**
     * Build row selection event
     * @param {number} row - row selected
     * @param {boolean} ctrlKey - true if ctrl key is pressed
     * @param {boolean} shiftKey - true if shift key is pressed
     * @returns {object} row selection event
     */
    buildRowSelectionEvent(row, ctrlKey, shiftKey) {
      const node = this.getNodeBindings().anchor;
      let startIndex = row;
      let endIndex = row;
      let mode = "set";

      if (shiftKey) {
        if (this.multiRowSelectionRoot === -1) {
          this.multiRowSelectionRoot = node.attribute("currentRow");
        }

        startIndex = this.multiRowSelectionRoot;
        endIndex = row;
        mode = ctrlKey ? "exset" : "set";

        this.updateMultiRowSelectionRoot = false;
      } else if (ctrlKey) {
        const children = node.getChildren();
        const rowInfoListNode = children[children.length - 1];
        const rowInfoNode = rowInfoListNode.getChildren()[row - node.attribute("offset")];
        mode = rowInfoNode.attribute("selected") === 1 ? "unset" : "exset";
      }

      return new cls.VMRowSelectionEvent(node.getId(), {
        startIndex: startIndex,
        endIndex: endIndex,
        selectionMode: mode
      });
    }

    /**
     * @inheritDoc
     */
    setFocus(preventScroll = false) {
      const widget = this.getWidget();
      widget.setFocus(false, preventScroll);
      if (widget.isInputMode() || widget.isInputArrayMode()) {
        this._focusChildWidget();
        return;
      }
      this.showReduceFilter();
    }

    /**
     * Show/hide reduce filter in chrome bar.
     */
    showReduceFilter() {
      const showFilter = this.getWidget().hasReduceFilter() && !this.getWidget().isTreeView();
      const noFilter = this.getAnchorNode().attribute("noFilter") === 1;
      // show filter menu item from chrome bar
      const filterValue = this.getAnchorNode().attribute("filter");
      context.FilterService.setValue(filterValue);
      const filterMenu = context.ChromeActionService.getGbcMenuItem("filter");
      filterMenu?.setHidden(noFilter || !showFilter);
    }

    _focusChildWidget() {
      const childWidget = this.getCurrentInternalWidget();
      if (!childWidget) {
        return;
      }
      childWidget.setFocus();
      // Notify the behavior that a child was focused
      this.emit(gbc.constants.controllerEvents.focusChild, childWidget);
    }

    /**
     * @inheritDoc
     */
    sendWidgetValue(newValue = null) {
      const valueNode = this.getAnchorNode().getCurrentValueNode(true);
      if (valueNode) {
        valueNode.getController().sendWidgetValue(newValue);
      }
    }

    /**
     * @inheritDoc
     */
    setStoredSetting(key, value) {
      if (this.forceDefaultSettings) {
        return;
      }
      const { formName, tableName } = this._storedSettingsKeys;

      // If there is a columnIndex here, it's a column
      if (typeof value.columnIndex !== "undefined") {
        gbc.StoredSettingsService.saveTableColumnSettings(formName, tableName, value.columnIndex, key, value);
      } else {
        gbc.StoredSettingsService.saveTableSettings(formName, tableName, key, value);
      }
    }

    /**
     * @inheritDoc
     */
    getStoredSetting(key, options = {}) {
      if (this.forceDefaultSettings) {
        return null;
      }
      const { formName, tableName } = this._storedSettingsKeys;

      // If there is a columnIndex here, it's a column
      if (typeof options.columnIndex !== "undefined") {
        return gbc.StoredSettingsService.loadTableColumnSettings(formName, tableName, options.columnIndex, key);
      }
      return gbc.StoredSettingsService.loadTableSettings(formName, tableName, key);
    }

    /**
     * Reset All Stored Setting for this table
     */
    resetStoredSetting() {
      if (!this.forceDefaultSettings) {
        const { formName, tableName } = this._storedSettingsKeys;
        gbc.StoredSettingsService.resetTableSettings(formName, tableName, "all");
      }
    }

    /**
     * Reset specific stored setting
     * @param {string} key - key to reset
     *
     */
    removeStoredSetting(key) {
      if (!this.forceDefaultSettings) {
        const { formName, tableName } = this._storedSettingsKeys;
        gbc.StoredSettingsService.resetTableSettings(formName, tableName, key);
      }
    }

    /**
     * @inheritDoc
     */
    ensureVisible(executeAction = false) {
      const widget = this.getAnchorNode().getWidget();
      widget.emit(context.constants.widgetEvents.splitViewChange, widget);
      return super.ensureVisible(executeAction);
    }

    /**
     * Return the current row stored in the Table node attributes
     * @returns {number}
     */
    getCurrentRow() {
      return this.getAnchorNode().attribute("currentRow");
    }

    /**
     * Return the current offset stored in the Table node attributes
     * @returns {number}
     */
    getOffset() {
      return this.getAnchorNode().attribute("offset");
    }
  }

  cls.TableController = TableController;
  cls.ControllerFactory.register("Table", cls.TableController);
});
